/**
 * The one audio player for TTS in the app (04-flows IA: "Now-playing bar — for TTS audio only
 * (Media Session, lock-screen controls)"). A lazily created `HTMLAudioElement` singleton, a
 * pure reducer for the state transitions (tested), and `useTtsPlayer()` over
 * `useSyncExternalStore`. Nothing here touches the DOM at import time — vitest runs in node.
 *
 * Autoplay policy: `speak()` and `ttsPlayer.play()` are always called from a tap. `speak` has
 * to await `api.tts` before it knows the URL, so it creates the element and calls `load()`
 * synchronously inside the gesture (unlocks the element on iOS/Chrome), then plays after the
 * await. If `play()` still rejects with NotAllowedError the status becomes 'paused' so the
 * bar's ▶ (a fresh gesture) can retry.
 *
 * Media Session (M4c): there is exactly ONE lock screen and now two things that can drive it —
 * this bar and an upload's <video>. `lib/media-session.ts` arbitrates: the last surface the
 * learner actually started owns the session, and every setter is a no-op for anyone else. So a
 * clip claims 'tts' as its track loads and hands the session back when it stops or ends; while
 * a video owns it, nothing here can overwrite its metadata.
 */
import { useSyncExternalStore } from 'react';
import { api } from './api';
import {
  claimMediaSession,
  releaseMediaSession,
  setMediaSessionHandlers,
  setMediaSessionMetadata,
  setMediaSessionPlaybackState,
  type MediaSessionOwner,
} from './media-session';

export interface TtsTrack {
  url: string;
  /** shown in the bar (`lang="ka"` — the Georgian text being spoken) */
  title: string;
  /** e.g. "saved line · TTS"; Media Session `album` */
  subtitle?: string;
  /** optional caller-owned id so a surface can tell "my row is the one playing" */
  key?: string;
}

export interface TtsPlayerState {
  status: 'idle' | 'loading' | 'playing' | 'paused' | 'error';
  track: TtsTrack | null;
  positionMs: number;
  durationMs: number | null;
}

export const TTS_IDLE: TtsPlayerState = {
  status: 'idle',
  track: null,
  positionMs: 0,
  durationMs: null,
};

// --- pure reducer -------------------------------------------------------------

export type TtsAction =
  | { type: 'load'; track: TtsTrack }
  | { type: 'playing' }
  | { type: 'paused' }
  /** `play()` rejected with NotAllowedError — keep the track, let ▶ retry from a gesture */
  | { type: 'blocked' }
  | { type: 'time'; positionMs: number; durationMs: number | null }
  | { type: 'ended' }
  | { type: 'error' }
  | { type: 'stop' };

/**
 * State machine: idle →(load) loading →(playing) playing ⇄(paused/playing) … →(ended|stop) idle.
 * Events that arrive when idle (stale element events after a stop) are ignored. `ended` goes
 * back to idle on purpose: TTS clips are seconds long and every surface that started one has
 * its own ▶, so the bar shouldn't linger over the transcript. Returns the same object when
 * nothing changed (useSyncExternalStore-friendly).
 */
export function ttsReduce(s: TtsPlayerState, a: TtsAction): TtsPlayerState {
  switch (a.type) {
    case 'load':
      return { status: 'loading', track: a.track, positionMs: 0, durationMs: null };
    case 'stop':
    case 'ended':
      return s.status === 'idle' ? s : TTS_IDLE;
    case 'playing':
      if (s.status === 'idle' || s.status === 'playing') return s;
      return { ...s, status: 'playing' };
    case 'paused':
    case 'blocked':
      if (s.status === 'idle' || s.status === 'paused') return s;
      return { ...s, status: 'paused' };
    case 'error':
      if (s.status === 'idle' || s.status === 'error') return s;
      return { ...s, status: 'error' };
    case 'time': {
      if (s.status === 'idle') return s;
      const positionMs = Math.max(0, Math.round(a.positionMs));
      const durationMs =
        a.durationMs != null && Number.isFinite(a.durationMs) && a.durationMs > 0
          ? Math.round(a.durationMs)
          : null;
      if (positionMs === s.positionMs && durationMs === s.durationMs) return s;
      return { ...s, positionMs, durationMs };
    }
  }
}

/** 0..1 for the progress line; null when the duration isn't known yet. */
export function ttsProgress(s: TtsPlayerState): number | null {
  if (s.durationMs == null || s.durationMs <= 0) return null;
  return Math.min(1, Math.max(0, s.positionMs / s.durationMs));
}

/**
 * The one calm sentence a surface shows when `speak()` throws (04-flows: unentitled = one calm
 * line, 429 = "resting", never an error code). Also fine for a non-ApiError (network).
 */
export function ttsCalmLine(err: unknown): string {
  const status = (err as { problem?: { status?: number } } | null)?.problem?.status;
  switch (status) {
    case 403:
      return 'Hearing it is part of the paid layer.';
    case 429:
      return 'The voice is resting for a bit — saved audio still plays.';
    case 503:
      return 'The voice isn’t set up yet.';
    default:
      return 'Couldn’t fetch the audio right now.';
  }
}

/** Map a rejected `audio.play()` to the reducer action. */
export function playRejectionAction(err: unknown): TtsAction {
  const name = (err as { name?: unknown } | null)?.name;
  return name === 'NotAllowedError' ? { type: 'blocked' } : { type: 'error' };
}

// --- singleton -----------------------------------------------------------------

let state: TtsPlayerState = TTS_IDLE;
const subs = new Set<(s: TtsPlayerState) => void>();
let audio: HTMLAudioElement | null = null;
/** bumps on every play()/speak()/stop(); late promise settlements from an older call are ignored */
let generation = 0;

/** who this module is on the shared Media Session */
const OWNER: MediaSessionOwner = 'tts';

/**
 * Take the lock screen for the bar. Called where the learner *starts* TTS — a track loading, or
 * ▶ on the bar after a pause — never on the player's own events, so a clip that is still playing
 * when a video starts simply stops being the owner instead of fighting for the session back.
 */
function takeMediaSession(): void {
  claimMediaSession(OWNER);
  // A claim tears the platform down (metadata null, handlers off) so a lock-screen ▶ is never
  // wired to a stopped player — which means the metadata we published last time is gone, even
  // though `lastMetaTrack` still says we published it. Forget it here, or resuming the SAME clip
  // after a video held the session republishes nothing and the lock screen stays blank.
  lastMetaTrack = null;
  // Re-installed on every claim: whoever owned the session before us set its own handlers.
  setMediaSessionHandlers(OWNER, {
    play: () => ttsPlayer.resume(),
    pause: () => ttsPlayer.pause(),
    stop: () => ttsPlayer.stop(),
  });
}

/**
 * Subscribers to "the clip finished on its own" (M6a step 12).
 *
 * This is a separate set from `subs`, and it exists because the reducer deliberately collapses
 * `stop` and `ended` into the same idle state — a state a subscriber cannot tell apart. That is
 * right for the now-playing bar, which only cares that nothing is playing, and wrong for the
 * Listen queue, which must advance on one and halt on the other. Without the distinction the
 * bar's ✕ (`components/TtsBar.tsx`, mounted app-wide) and the lock screen's stop button would
 * read as "clip ended" and start the next line — audio the learner cannot turn off.
 *
 * An edge rather than a flag on the state on purpose: "it finished" is a thing that happened
 * once, and a `lastAction: 'ended'` left sitting in the idle state would fire again for any
 * subscriber that mounted afterwards.
 */
const endedSubs = new Set<(finished: TtsTrack | null) => void>();

function dispatch(a: TtsAction): void {
  const next = ttsReduce(state, a);
  if (next === state) return;
  const wasIdle = state.status === 'idle';
  state = next;
  if (wasIdle && state.status !== 'idle') takeMediaSession();
  // Before the release, so the last sync (metadata null, playbackState 'none') still lands while
  // we own the session rather than being dropped as a non-owner's write.
  syncMediaSession();
  if (state.status === 'idle') releaseMediaSession(OWNER);
  for (const cb of subs) cb(state);
}

function ensureAudio(): HTMLAudioElement {
  if (audio) return audio;
  const el = new Audio();
  el.preload = 'auto';
  el.addEventListener('playing', () => dispatch({ type: 'playing' }));
  el.addEventListener('pause', () => {
    // `ended` also fires `pause` first (the `ended` listener takes it to idle), and swapping
    // `src` mid-play can too. While 'loading' the explicit paths (`pause()`, NotAllowedError →
    // 'blocked') report themselves, so the element's own event is only trusted once playing.
    if (!el.ended && state.status !== 'loading') dispatch({ type: 'paused' });
  });
  el.addEventListener('ended', () => {
    // Capture the track BEFORE the dispatch: `ended` takes the state to idle, which nulls it,
    // and a queue has to know WHICH clip finished to know what to play next.
    const finished = state.track;
    dispatch({ type: 'ended' });
    // After the dispatch, so an `onEnded` subscriber that starts the next clip observes the idle
    // state rather than the one it is about to replace. Iterated over a copy: a subscriber that
    // unsubscribes itself (a screen unmounting on the last clip) must not skip its neighbour.
    for (const cb of [...endedSubs]) {
      try {
        cb(finished);
      } catch (err) {
        console.warn('[tts] an onEnded subscriber threw', err);
      }
    }
  });
  el.addEventListener('error', () => dispatch({ type: 'error' }));
  const time = () =>
    dispatch({
      type: 'time',
      positionMs: el.currentTime * 1000,
      durationMs: Number.isFinite(el.duration) ? el.duration * 1000 : null,
    });
  el.addEventListener('timeupdate', time);
  el.addEventListener('durationchange', time);
  el.addEventListener('loadedmetadata', time);
  audio = el;
  return el;
}

/** The last track we published, so a `time` tick doesn't rebuild the metadata 10× a second. */
let lastMetaTrack: TtsTrack | null = null;
/**
 * Metadata when the track changes, playback state on every transition — as before, except that
 * both go through the shared module, which drops them unless 'tts' is the current owner (and
 * does nothing at all in a browser without the API).
 */
function syncMediaSession(): void {
  if (state.track !== lastMetaTrack) {
    lastMetaTrack = state.track;
    setMediaSessionMetadata(
      OWNER,
      state.track
        ? { title: state.track.title, artist: 'nelnela', album: state.track.subtitle ?? '' }
        : null,
    );
  }
  setMediaSessionPlaybackState(
    OWNER,
    state.status === 'playing' ? 'playing' : state.status === 'idle' ? 'none' : 'paused',
  );
}

async function playInternal(track: TtsTrack, gen: number): Promise<void> {
  const el = ensureAudio();
  dispatch({ type: 'load', track });
  el.src = track.url;
  el.load();
  try {
    await el.play();
  } catch (err) {
    if (gen !== generation) return; // superseded by a later play/stop — not ours to report
    dispatch(playRejectionAction(err));
  }
}

export const ttsPlayer = {
  /** Load and play a track (from a user gesture). Resolves when playback starts or is refused. */
  play(track: TtsTrack): Promise<void> {
    return playInternal(track, ++generation);
  },
  pause(): void {
    audio?.pause();
    dispatch({ type: 'paused' });
  },
  /** ▶ on the bar: also the retry after an autoplay refusal (it runs inside a fresh gesture). */
  resume(): void {
    if (!audio || !state.track || state.status === 'idle') return;
    // A tap on the bar's ▶ is the learner starting TTS again, so it takes the lock screen back
    // from a video — the reducer's paused→playing edge alone can't tell that apart from the
    // element resuming on its own.
    takeMediaSession();
    const gen = generation;
    if (state.status === 'error') {
      // a failed load: try the URL again from the top
      void playInternal(state.track, ++generation);
      return;
    }
    audio.play().catch((err) => {
      if (gen === generation) dispatch(playRejectionAction(err));
    });
  },
  stop(): void {
    generation++;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    dispatch({ type: 'stop' });
  },
  subscribe(cb: (s: TtsPlayerState) => void): () => void {
    subs.add(cb);
    return () => {
      subs.delete(cb);
    };
  },
  /**
   * Fires ONLY when a clip played to its end — never on `stop()`, never on `pause()`. The Listen
   * queue advances on this and on nothing else; see `endedSubs` above for why the ordinary
   * subscription cannot serve.
   */
  onEnded(cb: (finished: TtsTrack | null) => void): () => void {
    endedSubs.add(cb);
    return () => {
      endedSubs.delete(cb);
    };
  },
  getState(): TtsPlayerState {
    return state;
  },
};

/** The bar's hook — same object reference until something changed. */
export function useTtsPlayer(): TtsPlayerState {
  return useSyncExternalStore(ttsPlayer.subscribe, ttsPlayer.getState, ttsPlayer.getState);
}

/**
 * For a surface with its own ▶ (a saved row, the sheet's "Hear it"): the player's status when
 * *its* track (matched by `TtsTrack.key`) is loaded, else 'idle'. A primitive snapshot, so the
 * row doesn't re-render on every timeupdate of somebody else's clip.
 */
export function useTtsKeyStatus(key: string): TtsPlayerState['status'] {
  const get = () => (state.track?.key === key ? state.status : 'idle');
  return useSyncExternalStore(ttsPlayer.subscribe, get, get);
}

/**
 * "▶ Hear it": synthesize (server caches by text+voice) then play. Must be called from a tap.
 * Throws `ApiError` on 403 (unentitled) / 429 (budget) / 503 (unconfigured) — the caller shows
 * the calm line; the bar goes back to idle in that case (never an error surface for it).
 */
export async function speak(
  text: string,
  opts: { title: string; subtitle?: string; voice?: string; key?: string },
): Promise<void> {
  const gen = ++generation;
  const el = ensureAudio();
  // Inside the gesture: `load()` lifts the element's autoplay restriction (WebKit removes its
  // behaviour restrictions on a user-gesture load) so the play() after the await is allowed.
  // The old src is dropped first so nothing is re-fetched for no reason.
  el.removeAttribute('src');
  el.load();
  // Show the bar right away (loading) so the tap has visible feedback while the server works.
  dispatch({
    type: 'load',
    track: { url: '', title: opts.title, subtitle: opts.subtitle, key: opts.key },
  });
  let url: string;
  try {
    const a = await api.tts({ targetLang: 'ka', text, voice: opts.voice });
    url = a.url;
  } catch (err) {
    if (gen === generation) dispatch({ type: 'stop' });
    throw err;
  }
  if (gen !== generation) return; // the user started something else meanwhile
  await playInternal({ url, title: opts.title, subtitle: opts.subtitle, key: opts.key }, gen);
}
